import { createApp, App } from "vue";
import { createPinia } from "pinia";
import { plugin, defaultConfig } from "@formkit/vue";
import { CopyProjectModalProps } from "../src/types/modal.types";
import { vAutoHeight } from "../src/directives/autoHeight";

export interface AppControl {
    unmount: () => void;
    appContainer: HTMLElement;
    appInstance: App<Element>;
}

// Tooltips for form fields
document.addEventListener("DOMContentLoaded", function () {
    // Link calculator
    const linkCalculatorAttribute = "data-link-calculator";
    const linkCalculatorWrapper = document.querySelector<HTMLElement>("[" + linkCalculatorAttribute + "]");

    if (linkCalculatorWrapper) {
        import("../src/vue-views/LinkCalculator.vue").then(({ default: component }) => {
            const calculationUid = linkCalculatorWrapper.getAttribute(linkCalculatorAttribute);
            linkCalculatorWrapper.removeAttribute(linkCalculatorAttribute);
            const appInstance = createApp(component, { calculationUid });
            appInstance.directive("auto-height", vAutoHeight);
            // appInstance.use(pinia);
            appInstance.use(plugin, defaultConfig);
            appInstance.mount(linkCalculatorWrapper);
        });
    }

    // Flex/multi calculator
    const flexMultiCalculatorAttribute = "data-flex-multi-calculator";
    const flexMultiCalculatorWrapper = document.querySelector<HTMLElement>("[" + flexMultiCalculatorAttribute + "]");

    if (flexMultiCalculatorWrapper) {
        import("../src/vue-views/FlexMultiCalculator.vue").then(({ default: component }) => {
            const calculationUid = flexMultiCalculatorWrapper.getAttribute(flexMultiCalculatorAttribute);
            flexMultiCalculatorWrapper.removeAttribute(flexMultiCalculatorAttribute);
            const appInstance = createApp(component, { calculationUid });
            appInstance.directive("auto-height", vAutoHeight);
            appInstance.use(plugin, defaultConfig);
            appInstance.mount(flexMultiCalculatorWrapper);
        });
    }

    // Horizontal calculator
    const horizontalCalculatorAttribute = "data-horizontal-calculator";
    const horizontalCalculatorWrapper = document.querySelector<HTMLElement>("[" + horizontalCalculatorAttribute + "]");

    if (horizontalCalculatorWrapper) {
        import("../src/vue-views/HorizontalCalculator.vue").then(({ default: component }) => {
            const calculationUid = horizontalCalculatorWrapper.getAttribute(horizontalCalculatorAttribute);
            horizontalCalculatorWrapper.removeAttribute(horizontalCalculatorAttribute);
            const appInstance = createApp(component, { calculationUid });
            appInstance.directive("auto-height", vAutoHeight);
            appInstance.use(plugin, defaultConfig);
            appInstance.mount(horizontalCalculatorWrapper);
        });
    }

    // REDAir Easy calculator (clone of Flex/Multi, locked to MULTI)
    const redairEasyCalculatorAttribute = "data-redair-easy-calculator";
    const redairEasyCalculatorWrapper = document.querySelector<HTMLElement>("[" + redairEasyCalculatorAttribute + "]");

    if (redairEasyCalculatorWrapper) {
        import("../src/vue-views/RedairEasyCalculator.vue").then(({ default: component }) => {
            const calculationUid = redairEasyCalculatorWrapper.getAttribute(redairEasyCalculatorAttribute);
            redairEasyCalculatorWrapper.removeAttribute(redairEasyCalculatorAttribute);
            const appInstance = createApp(component, { calculationUid });
            appInstance.directive("auto-height", vAutoHeight);
            appInstance.use(plugin, defaultConfig);
            appInstance.mount(redairEasyCalculatorWrapper);
        });
    }

    // En global SharedApp som indeholder vue komponenter der skal bruges på tværs af sider
    const sharedAppContainerAttribute = "data-shared-app-container";
    const sharedAppContainerWrapper = document.querySelector<HTMLElement>("[" + sharedAppContainerAttribute + "]");

    if (sharedAppContainerWrapper) {
        import("../src/vue-views/SharedApp.vue").then(({ default: component }) => {
            const appInstance = createApp(component, {});
            appInstance.directive("auto-height", vAutoHeight);
            appInstance.use(plugin, defaultConfig);
            appInstance.mount(sharedAppContainerWrapper);
        });
    }

    // Project - List
    const projectListAttribute = "data-project-list";
    const projectListWrapper = document.querySelector<HTMLElement>("[" + projectListAttribute + "]");

    if (projectListWrapper) {
        import("../src/vue-views/ProjectList.vue").then(({ default: component }) => {
            let copyModalProps: CopyProjectModalProps = undefined;
            const lang = projectListWrapper.getAttribute(projectListAttribute);
            const sharedProjectWrapper = document.querySelector("[data-project-shared-dialog]");

            if (sharedProjectWrapper) {
                const attributeValue = sharedProjectWrapper.getAttribute("data-project-shared-dialog");
                const [sharedLang, projectUid, sharingUid] = attributeValue.split(";");
                copyModalProps = { sharedLang, projectUid, sharingUid };
            }

            projectListWrapper.removeAttribute(projectListAttribute);
            const appInstance = createApp(component, { lang: lang, copyModalProps: copyModalProps });
            appInstance.directive("auto-height", vAutoHeight);
            appInstance.use(plugin, defaultConfig);
            appInstance.mount(projectListWrapper);
        });
    }

    // Latest - Calculation - List
    const lastestCalculationListAttribute = "data-latest-calculation-list";
    const lastestCalculationListWrapper = document.querySelector<HTMLElement>("[" + lastestCalculationListAttribute + "]");

    if (lastestCalculationListWrapper) {
        import("../src/vue-views/LatestCalculationList.vue").then(({ default: component }) => {
            const lastestCalculationListParameters = lastestCalculationListWrapper.getAttribute(lastestCalculationListAttribute);

            const lang = lastestCalculationListParameters;

            lastestCalculationListWrapper.removeAttribute(lastestCalculationListAttribute);
            const appInstance = createApp(component, { lang: lang });
            appInstance.directive("auto-height", vAutoHeight);
            appInstance.use(plugin, defaultConfig);
            appInstance.mount(lastestCalculationListWrapper);
        });
    }

    // REDAir Leads Dashboard
    const redairLeadsDashboardEl = document.getElementById("redair-leads-dashboard");

    if (redairLeadsDashboardEl) {
        import("../src/vue-views/RedairLeadsDashboard.vue").then(({ default: component }) => {
            const configEl = document.getElementById("redair-leads-config");
            const props = JSON.parse(configEl.textContent);
            const appInstance = createApp(component, props);
            appInstance.use(createPinia());
            appInstance.use(plugin, defaultConfig);
            appInstance.mount(redairLeadsDashboardEl);
        });
    }

    // Project - Details
    const projectDetailsAttribute = "data-project-details";
    const projectDetailsWrapper = document.querySelector<HTMLElement>("[" + projectDetailsAttribute + "]");

    if (projectDetailsWrapper) {
        import("../src/vue-views/ProjectDetails.vue").then(({ default: component }) => {
            const projectDetailsParameters = projectDetailsWrapper.getAttribute(projectDetailsAttribute);

            const projectParametersSplit = projectDetailsParameters.split(";");

            const projectLang = projectParametersSplit[0];
            const projectProjectUid = projectParametersSplit[1];

            projectDetailsWrapper.removeAttribute(projectDetailsAttribute);
            const appInstance = createApp(component, { lang: projectLang, projectUid: projectProjectUid });
            appInstance.directive("auto-height", vAutoHeight);
            appInstance.use(plugin, defaultConfig);
            appInstance.mount(projectDetailsWrapper);
        });
    }
});
